import { FamilyShell, type FamilyShellPage } from "@crew/ui/components/family-shell";
import { useMemo, useState, useSyncExternalStore } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { NewAreaWindow } from "../features/areas/area-form";
import { areaNavIcon } from "../features/areas/parts";
import { useLogOut } from "../features/auth/logout";
import { LogOutFailed } from "../features/auth/logout-controls";
import { hasUnseen, subscribeSeen } from "../features/changelog/changelog";
import { resumePanel, usePanel } from "../features/chats/panel-state";
import { CrewPanel } from "../features/chats/side-panel";
import { computerAwake, sidebarComputer } from "../features/computer/status";
import { useHubEvents } from "./events";
import type { Me } from "./me";
import { HOME } from "./home";
import { hasPanel, personTint, shellAgent } from "./tints";
import { useAppsOn } from "../features/apps/api";

/** Pages out of the nav: Decisions, which approvals replaced. Its path goes home. */
export const NOT_YET: readonly FamilyShellPage[] = ["decisions"];

/** The Family shell on the real `/api/me`, with the live hub stream and the side panel. */
export function AppShell({ me }: { me: Me }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [, setPanel] = usePanel();
  useHubEvents(true);
  const logOut = useLogOut();
  const unseen = useSyncExternalStore(subscribeSeen, () => hasUnseen(me.user.id));
  const agents = me.agents.length;
  const people = me.counts.people;
  const [addingArea, setAddingArea] = useState(false);
  // Slice 6d (spec §8.1): the sidebar's Areas, each with its icon and open tasks.
  const areas = useMemo(
    () =>
      me.areas.map((a) => ({
        id: a.id,
        label: a.label,
        href: `/areas/${a.slug}`,
        icon: areaNavIcon(a.icon),
        count: a.open_tasks || undefined,
      })),
    [me.areas],
  );
  // Slice 6i: Apps in the nav only where crew-apps is bound.
  const appsOn = useAppsOn();
  return (
    <FamilyShell
      pathname={pathname}
      workspace={{
        name: me.workspace.name,
        // The people not removed, and the agents (slice 6a §6.5).
        detail: `${people} ${people === 1 ? "person" : "people"} · ${agents} ${agents === 1 ? "agent" : "agents"}`,
      }}
      user={{
        name: me.user.name,
        role: me.user.role === "owner" ? "Owner" : "Member",
        initials: me.user.initials,
        tint: personTint(me.user.tint),
      }}
      agents={me.agents.map((agent) =>
        shellAgent(agent, computerAwake(me.computer), me.computer?.held_by === me.user.id),
      )}
      computer={sidebarComputer(me.computer)}
      areas={areas}
      onAddArea={() => setAddingArea(true)}
      counts={{ decisions: me.counts.open_approvals_mine }}
      panel={hasPanel(pathname) ? <CrewPanel me={me} /> : undefined}
      onLogOut={() => logOut.mutate()}
      whatsNew={{ unseen }}
      hide={appsOn ? NOT_YET : [...NOT_YET, "apps"]}
      home={HOME}
      // The phone's "Threads": the panel where you left off, or Chats itself.
      onOpenPanel={() =>
        hasPanel(pathname) ? setPanel(resumePanel(me.workspace.id)) : navigate("/chats")
      }
    >
      <Outlet />
      <NewAreaWindow open={addingArea} onOpenChange={setAddingArea} />
      <LogOutFailed
        logOut={logOut}
        className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-1/2 md:bottom-4 z-50 -translate-x-1/2 rounded-lg bg-popover px-4 py-2 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10"
      />
    </FamilyShell>
  );
}
